import {
  CustomerFormValues,
  CustomerContact,
  CustomerTypedLocation,
  emptyCustomerLegalEntity,
  emptyCustomerCorporateHierarchy,
  emptyCustomerCommercial,
  emptyCustomerCompliance,
} from './types';
import {
  emptyToNull,
  toNumberOrNull,
  toIntOrNull,
  toDateInput,
  numberToString,
  toIntOrZero,
} from '../payloadHelpers';

function mapLegalEntity(legalEntity: CustomerFormValues['legalEntity']) {
  const mapped = {
    legalName: emptyToNull(legalEntity.legalName),
    tradingName: emptyToNull(legalEntity.tradingName),
    companyRegistrationNumber: emptyToNull(legalEntity.companyRegistrationNumber),
    vatNumber: emptyToNull(legalEntity.vatNumber),
    taxId: emptyToNull(legalEntity.taxId),
    jurisdictionCountry: emptyToNull(legalEntity.jurisdictionCountry),
    legalEntityType: emptyToNull(legalEntity.legalEntityType),
    incorporationDate: emptyToNull(legalEntity.incorporationDate),
  };
  return Object.values(mapped).some((value) => value !== null) ? mapped : null;
}

function mapContacts(contacts: CustomerContact[]) {
  const rows = contacts
    .map((contact) => ({
      contactId: emptyToNull(contact.contactId),
      name: emptyToNull(contact.name),
      email: emptyToNull(contact.email),
      phoneCountryCode: emptyToNull(contact.phoneCountryCode),
      phoneNumber: emptyToNull(contact.phoneNumber),
      role: emptyToNull(contact.role),
      locationId: emptyToNull(contact.locationId),
      isActive: contact.isActive,
    }))
    .filter(
      (contact) =>
        contact.name !== null ||
        contact.email !== null ||
        contact.phoneNumber !== null ||
        contact.role !== null
    );
  return rows.length > 0 ? rows : null;
}

function mapLocations(locations: CustomerTypedLocation[]) {
  const rows = locations
    .map((location) => ({
      locationId: emptyToNull(location.locationId),
      type: emptyToNull(location.type),
      line1: emptyToNull(location.line1),
      line2: emptyToNull(location.line2),
      city: emptyToNull(location.city),
      county: emptyToNull(location.county),
      postalCode: emptyToNull(location.postalCode),
      country: emptyToNull(location.country),
      coordinates:
        location.coordinates.lat === 0 && location.coordinates.lon === 0
          ? null
          : location.coordinates,
      isPrimary: location.isPrimary,
      isActive: location.isActive,
    }))
    .filter(
      (location) =>
        location.line1 !== null ||
        location.city !== null ||
        location.postalCode !== null ||
        location.type !== null
    );
  return rows.length > 0 ? rows : null;
}

function mapCorporateHierarchy(
  hierarchy: CustomerFormValues['corporateHierarchy']
) {
  const mapped = {
    parentCustomerId: emptyToNull(hierarchy.parentCustomerId),
    ultimateParentCustomerId: emptyToNull(hierarchy.ultimateParentCustomerId),
    hierarchyLevel: toIntOrNull(hierarchy.hierarchyLevel),
    regionalGrouping: emptyToNull(hierarchy.regionalGrouping),
    isGroupAccount: hierarchy.isGroupAccount,
  };
  const hasValue =
    mapped.parentCustomerId !== null ||
    mapped.ultimateParentCustomerId !== null ||
    mapped.hierarchyLevel !== null ||
    mapped.regionalGrouping !== null ||
    mapped.isGroupAccount;
  return hasValue ? mapped : null;
}

function mapCommercial(commercial: CustomerFormValues['commercial']) {
  const mapped = {
    paymentTerms: emptyToNull(commercial.paymentTerms),
    creditLimit: toNumberOrNull(commercial.creditLimit),
    creditCurrency: emptyToNull(commercial.creditCurrency),
    contractType: emptyToNull(commercial.contractType),
    defaultIncoterms: emptyToNull(commercial.defaultIncoterms),
    pricingTier: emptyToNull(commercial.pricingTier),
  };
  return Object.values(mapped).some((value) => value !== null) ? mapped : null;
}

function mapCompliance(compliance: CustomerFormValues['compliance']) {
  const mapped = {
    kycStatus: emptyToNull(compliance.kycStatus),
    kycVerifiedAt: emptyToNull(compliance.kycVerifiedAt),
    sanctionsCheckStatus: emptyToNull(compliance.sanctionsCheckStatus),
    sanctionsCheckedAt: emptyToNull(compliance.sanctionsCheckedAt),
  };
  return Object.values(mapped).some((value) => value !== null) ? mapped : null;
}

export function toCustomerPayload(data: CustomerFormValues) {
  return {
    ...data,
    legalEntity: mapLegalEntity(data.legalEntity),
    contacts: mapContacts(data.contacts),
    locations: mapLocations(data.locations),
    corporateHierarchy: mapCorporateHierarchy(data.corporateHierarchy),
    commercial: mapCommercial(data.commercial),
    compliance: mapCompliance(data.compliance),
    recordType: data.recordType,
    market: data.market,
    channel: data.channel,
    lifecycleStatus: data.lifecycleStatus,
    partnerPayerRef: data.partnerPayerRef,
    partnerBillToRef: data.partnerBillToRef,
    readinessGaps: toIntOrZero(data.readinessGaps),
    hierarchyGroupName: data.hierarchyGroupName,
    hierarchyParentName: data.hierarchyParentName,
    hierarchySubNodeName: data.hierarchySubNodeName,
    hierarchyBanner: data.hierarchyBanner,
    hierarchyBucket: data.hierarchyBucket,
    hierarchyIsShipToSite: data.hierarchyIsShipToSite,
    hierarchyIsDemandNode: data.hierarchyIsDemandNode,
  };
}

export function fromApiCustomer(
  customer: Record<string, any>
): Partial<CustomerFormValues> {
  return {
    legalEntity: customer.legalEntity
      ? {
          legalName: customer.legalEntity.legalName ?? '',
          tradingName: customer.legalEntity.tradingName ?? '',
          companyRegistrationNumber:
            customer.legalEntity.companyRegistrationNumber ?? '',
          vatNumber: customer.legalEntity.vatNumber ?? '',
          taxId: customer.legalEntity.taxId ?? '',
          jurisdictionCountry: customer.legalEntity.jurisdictionCountry ?? '',
          legalEntityType: customer.legalEntity.legalEntityType ?? '',
          incorporationDate: toDateInput(
            customer.legalEntity.incorporationDate
          ),
        }
      : { ...emptyCustomerLegalEntity },
    contacts: (customer.contacts ?? []).map((contact: any) => ({
      contactId: contact.contactId ?? '',
      name: contact.name ?? '',
      email: contact.email ?? '',
      phoneCountryCode: contact.phoneCountryCode ?? '',
      phoneNumber: contact.phoneNumber ?? '',
      role: contact.role ?? '',
      locationId: contact.locationId ?? '',
      isActive: contact.isActive ?? true,
    })),
    locations: (customer.locations ?? []).map((location: any) => ({
      locationId: location.locationId ?? '',
      type: location.type ?? '',
      line1: location.line1 ?? '',
      line2: location.line2 ?? '',
      city: location.city ?? '',
      county: location.county ?? '',
      postalCode: location.postalCode ?? '',
      country: location.country ?? '',
      coordinates: {
        lat: location.coordinates?.lat ?? 0,
        lon: location.coordinates?.lon ?? 0,
      },
      isPrimary: location.isPrimary ?? false,
      isActive: location.isActive ?? true,
    })),
    corporateHierarchy: customer.corporateHierarchy
      ? {
          parentCustomerId: customer.corporateHierarchy.parentCustomerId ?? '',
          ultimateParentCustomerId:
            customer.corporateHierarchy.ultimateParentCustomerId ?? '',
          hierarchyLevel: numberToString(
            customer.corporateHierarchy.hierarchyLevel
          ),
          regionalGrouping: customer.corporateHierarchy.regionalGrouping ?? '',
          isGroupAccount: customer.corporateHierarchy.isGroupAccount ?? false,
        }
      : { ...emptyCustomerCorporateHierarchy },
    commercial: customer.commercial
      ? {
          paymentTerms: customer.commercial.paymentTerms ?? '',
          creditLimit: numberToString(customer.commercial.creditLimit),
          creditCurrency: customer.commercial.creditCurrency ?? '',
          contractType: customer.commercial.contractType ?? '',
          defaultIncoterms: customer.commercial.defaultIncoterms ?? '',
          pricingTier: customer.commercial.pricingTier ?? '',
        }
      : { ...emptyCustomerCommercial },
    compliance: customer.compliance
      ? {
          kycStatus: customer.compliance.kycStatus ?? '',
          kycVerifiedAt: toDateInput(customer.compliance.kycVerifiedAt),
          sanctionsCheckStatus:
            customer.compliance.sanctionsCheckStatus ?? '',
          sanctionsCheckedAt: toDateInput(
            customer.compliance.sanctionsCheckedAt
          ),
        }
      : { ...emptyCustomerCompliance },
    recordType: customer.recordType ?? '',
    market: customer.market ?? '',
    channel: customer.channel ?? '',
    lifecycleStatus: customer.lifecycleStatus ?? '',
    partnerPayerRef: customer.partnerPayerRef ?? '',
    partnerBillToRef: customer.partnerBillToRef ?? '',
    readinessGaps: numberToString(customer.readinessGaps),
    hierarchyGroupName: customer.hierarchyGroupName ?? '',
    hierarchyParentName: customer.hierarchyParentName ?? '',
    hierarchySubNodeName: customer.hierarchySubNodeName ?? '',
    hierarchyBanner: customer.hierarchyBanner ?? '',
    hierarchyBucket: customer.hierarchyBucket ?? '',
    hierarchyIsShipToSite: customer.hierarchyIsShipToSite ?? false,
    hierarchyIsDemandNode: customer.hierarchyIsDemandNode ?? false,
  };
}
